import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IUserSessionComponentProps, UserSessionViewModelKey } from '../../../../../models/AppState';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { BulkEmailApprovalUsersSelectRow } from '../BulkEmailApprovalUsersSelectRow';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IUserSessionComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	filterRequest: Api.IBulkContactsRequest;
	selectedUsers: Api.IUser[];
	setSelectedUsers: (users: Api.IUser[]) => void;
	selectState: 'all' | 'some' | 'none';
	setSelectState: (state: 'all' | 'some' | 'none') => void;
	impersonationContext: Api.IImpersonationContext;
	availableUsers: Api.IUser[];
}

const _BulkEmailApprovalUsersSelect: React.FC<IProps> = ({
	className,
	styles,
	selectedUsers,
	setSelectedUsers,
	selectState,
	setSelectState,
	availableUsers,
}) => {
	const onSelectAllCheckedChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newState = e.target.checked ? 'all' : 'none';
			const newUsers = e.target.checked ? availableUsers : [];

			// Batch updates
			setSelectState(newState);
			setSelectedUsers(newUsers);
		},
		[availableUsers, setSelectedUsers, setSelectState]
	);

	const usersList = React.useMemo(() => {
		if (availableUsers.length === 0) {
			return <li className={css(styleSheet.usersListRow)}>No users found.</li>;
		}

		return availableUsers.map((x, index) => (
			<BulkEmailApprovalUsersSelectRow
				key={`${x.id}-${index}`}
				selectedUsers={selectedUsers}
				setSelectedUsers={setSelectedUsers}
				styles={[styleSheet.usersListRow]}
				user={x}
			/>
		));
	}, [availableUsers, selectedUsers, setSelectedUsers]);

	return (
		<div
			className={`${css(styleSheet.container, ...(styles || []))} bulk-email-approval-users-select ${className || ''}`}
		>
			<div className={css(styleSheet.header)}>Who should review and approve the campaigns?</div>
			<div className={css(styleSheet.selectAllContainer)}>
				<Checkbox
					checked={selectState === 'all'}
					childrenPosition='right'
					id='contact-list-header-select-all-checkbox'
					onChange={onSelectAllCheckedChanged}
					partial={selectState === 'some'}
				>
					<span className={css(styleSheet.selectAllSpan)}>all</span>
				</Checkbox>
			</div>
			<ul className={css(styleSheet.usersList)}>{usersList}</ul>
		</div>
	);
};

const BulkEmailApprovalUsersSelectAsObserver = observer(_BulkEmailApprovalUsersSelect);
const BulkEmailApprovalUsersSelectWithContext = inject(UserSessionViewModelKey)(BulkEmailApprovalUsersSelectAsObserver);
export const BulkEmailApprovalUsersSelect = withEventLogging(
	BulkEmailApprovalUsersSelectWithContext,
	'BulkEmailApprovalUsersSelect'
);
